import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ThemeContext } from 'styled-components';
import { AttentionTriangleIcon, XCloseIcon } from '@rsos/assets/icons';
import { Box, Text, Absolute } from '@rsos/components/capstone/base';
import {
  DialogContentRemark,
  DialogTitle,
  DisclaimerButton,
  ExpandedDialogContainer,
  Left,
  Right,
} from '@rsos/components/capstone/common/Map/MapDialog/MapDialog.styles';
import { MenuButton } from '@rsos/components/capstone/common/Map/MapDialog/MenuButton';
import { parentSpatialLayerSelector } from '@rsos/components/capstone/common/Map/SpatialLayers/selectors';
import { trackDisclaimerClick } from '@rsos/components/capstone/common/Map/SpatialLayers/spatialLayersTrackings';
import { selectUserOrgByApp } from '@rsos/sinatra';
import {
  setActiveCRGObjectFloor,
  fetchCRGDeeplink,
} from '../../../actions/spatialLayers';
import { store } from '../../../store';
import { FloorSelector, GeoTiffMapCard } from '../GeoTiffMapCard';
import Disclaimer from '../GeoTiffMapCard/Disclaimer';

/**
 * @param {Function} onClose - onClose function for re-adding event listeners to the marker icon
 * @param {String} - ID of removed Call
 */

const DisclaimerContainer = styled(Absolute)`
  padding: 5px;
`;

const CloseBox = styled(Box).attrs(() => ({
  color: 'secondaryLink',
}))`
  cursor: pointer;
`;

const MAP_CARD_MAX_HEIGHT = 340;
const MAP_CARD_MAX_WIDTH = 360;

const ExpandedMapDialog = ({ onClose, removedID }) => {
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const dispatch = useDispatch();
  const activeCrgObject = useSelector(
    state => state.spatialLayers.activeCrgObject,
  );
  const { spatialObject, activeFloor } = activeCrgObject;
  const spatialLayer = useSelector(state =>
    parentSpatialLayerSelector(state.spatialLayers),
  );
  const orgID = selectUserOrgByApp(
    useSelector(state => state.sinatra),
    'capstone',
  ).id;
  const irpVersion = useSelector(state => state.sinatra.irp.irpVersion);
  const theme = useContext(ThemeContext);

  const handleBtnClick = bool => {
    setShowDisclaimer(bool);
    trackDisclaimerClick(irpVersion, showDisclaimer, spatialLayer.source);
  };

  useEffect(() => {
    if (orgID && spatialObject.id) {
      dispatch(fetchCRGDeeplink(orgID, spatialObject.id));
    }
  }, [dispatch, orgID, spatialObject.id]);

  useEffect(() => {
    // This hook should only trigger once after the Portal has been rendered
    if (
      !!activeFloor &&
      !Object.keys(activeFloor).length &&
      spatialObject?.attributes?.floors
    ) {
      const defaultFloor = spatialObject.attributes.floors.find(
        floor => floor.default_floor,
      );
      dispatch(setActiveCRGObjectFloor(defaultFloor));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spatialObject, dispatch]);

  const onOptionChange = data => {
    dispatch(setActiveCRGObjectFloor(data));
  };

  const floorOptions = useMemo(() => {
    if (!spatialObject.attributes.floors) {
      return [];
    }
    return spatialObject.attributes.floors
      .map(floor => {
        return {
          value: `${floor.floor_order} - ${floor.floor_name}`,
          label: `${floor.floor_order} - ${floor.floor_name}`,
          isSelected: activeFloor?.link_uri === floor.link_uri,
          data: floor,
        };
      })
      .sort((a, b) => {
        return a.data?.floor_order - b.data?.floor_order;
      });
  }, [spatialObject, activeFloor]);

  const selectedFloor = useMemo(() => {
    if (!floorOptions.length) {
      return {};
    }
    return floorOptions.find(floor => floor.isSelected);
  }, [floorOptions]);

  const areButtonsDisabled = useMemo(() => {
    return !Object.keys(selectedFloor).length || !floorOptions.length;
  }, [floorOptions, selectedFloor]);

  useEffect(() => {
    if (!spatialLayer.isChecked) {
      onClose();
    }
  }, [onClose, spatialLayer.isChecked]);

  useEffect(() => {
    if (areButtonsDisabled) {
      setShowDisclaimer(false);
    }
  }, [areButtonsDisabled]);

  if (!spatialLayer.isChecked) {
    return null;
  }

  return (
    <Provider store={store}>
      <ExpandedDialogContainer horizontal>
        <Left>
          <Box pb={15}>
            <Box horizontal>
              <DialogTitle>{spatialObject.name}</DialogTitle>
            </Box>
            <Box horizontal>
              <DialogContentRemark>
                Source: {spatialLayer.source}
              </DialogContentRemark>
            </Box>
          </Box>
          <MenuButton
            buttonText={'View'}
            deeplink={activeCrgObject?.deeplink}
            irpVersion={irpVersion}
            source={spatialLayer.source}
            subtitle={spatialObject.attributes.link_subtext}
            title={spatialObject.attributes.link_text}
          />
        </Left>
        <Right grow>
          <Box
            height={25}
            p={0}
            horizontal
            width={'100%'}
            justify="space-between"
          >
            <Box>
              <FloorSelector
                floorOptions={floorOptions}
                irpVersion={irpVersion}
                isDisabled={areButtonsDisabled}
                onOptionChange={onOptionChange}
                source={spatialLayer.source}
              />
            </Box>
            <Box grow horizontal justify="space-evenly" align="center">
              <Text color="secondaryText">
                {floorOptions.findIndex(floor => floor.isSelected) + 1} of{' '}
                {floorOptions.length}
              </Text>
              <DisclaimerButton
                btnType="tertiary"
                m={0}
                pl={'12px'}
                disabled={areButtonsDisabled}
                onClick={() => handleBtnClick(!showDisclaimer)}
                showDisclaimer={showDisclaimer}
              >
                <AttentionTriangleIcon
                  width={13}
                  height={12}
                  color={theme.colors.secondaryLink}
                />
              </DisclaimerButton>
            </Box>
            <CloseBox>
              <XCloseIcon onClick={onClose} />
            </CloseBox>
          </Box>
          <Box
            alignSelf="center"
            height={MAP_CARD_MAX_HEIGHT}
            mt="10px"
            width={MAP_CARD_MAX_WIDTH}
            position="relative"
          >
            {showDisclaimer && (
              <DisclaimerContainer zIndex={1}>
                <Disclaimer onCloseDisclaimer={() => handleBtnClick(false)} />
              </DisclaimerContainer>
            )}
            <GeoTiffMapCard
              activeCrgObject={activeCrgObject}
              removedID={removedID}
              selectedFloor={selectedFloor}
            />
          </Box>
        </Right>
      </ExpandedDialogContainer>
    </Provider>
  );
};

ExpandedMapDialog.propTypes = {
  onClose: PropTypes.func,
};

export default ExpandedMapDialog;
